import { IImportVO } from 'library/ts/interfaces/importer/IImportVO';
import { ImportType } from 'library/ts/model/enums/config/ImportType';
import { ImportVO } from 'library/ts/model/vo/ImportVO';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { SkinMode } from 'tag/ts/tag/model/type/SkinMode';
import { IAviaDependencyManager } from 'tag/ts/tag/player/adapters/avia/managers/interfaces/IAviaDependencyManager';
import { IAviaDependencyManagerArgument } from 'tag/ts/tag/player/adapters/avia/managers/interfaces/IAviaDependencyManagerArgument';

/**
 * @copyright © Paramount Streaming
 * @author Stan Gletski
 * @description Manages Avia dependency calculation and configuration
 *
 * **Responsibilities:**
 * - **Dependency Calculation**: Determines which Avia scripts to load based on configuration
 * - **Player Type Filtering**: Conditionally includes avia-js-ui for FULL player type only
 * - **Cache Busting**: Adds cache tokens to script URLs for development
 * - **Duplicate Detection**: Prevents loading scripts that are already present
 * - **Configuration Processing**: Applies Tag utilities final processing to imports
 *
 * **Dependencies Managed:**
 * - **avia.js**: Core Avia player library
 * - **avia-js-ui**: Optional UI library (FULL player type only)
 * - **Plugins**: Additional Avia plugins as configured
 *
 * **Lifecycle:**
 * - Called once during AviaAdapter initialization
 * - Returns import array for AbstractAdapter to load
 *
 * **Architecture:**
 * Extracted from AviaAdapter to follow Single Responsibility Principle.
 * Reduces AviaAdapter complexity by ~85 lines.
 * Focuses on WHAT to load, while AbstractAdapter handles HOW to load.
 */
export class AviaDependencyManager extends AbstractTagClass implements IAviaDependencyManager {

	constructor( argument: IAviaDependencyManagerArgument ) {
		super(
			{
				...argument,
				name: 'AviaDependencyManager'
			}
		);
	}

	/**
	 * @description Calculates required Avia dependencies
	 * @returns {IImportVO[]} Array of imports for adapter to load
	 */
	calculateDependencies = (): IImportVO[] => {
		// calculate dependencies we are about to load
		let imports: IImportVO[] = [];
		// Imports specified in Tag config Imports module
		const config = this.tag?.model?.config?.tag?.Imports?.params;

		// loop over tag config imports in avia section
		Object.keys( config.avia ).forEach(
			( app ) => {
				const scriptURL = config.avia[ app ].url.regular;
				const scriptNamespace = config.avia[ app ].namespace;

				/**
				 * @description Adds a script to our imports array
				 * @param {string} url - URL of the script to import
				 * @param {string} namespace - Namespace for the script
				 */
				const addImport = ( url: string, namespace: string ) => {
					if (
						!this.utils.Type.isEmpty( url )
						&&
						!this.utils.Import.alreadyPresent(
							url,
							namespace,
							!this.utils.URL.valid( url )
						)
					) {
						// add import...
						imports.push(
							new ImportVO(
								ImportType.SCRIPT,
								this.utils.URL.addCacheToken( url, this.runtimeVO.debug.cacheBust ),
								namespace,
								window
							)
						);
					}
				};

				// filter per avia script / section name + process individual use cases if needed
				switch ( app ) {
					case 'player_ui': // avia player ui script should be processed separately
						if ( this?.tag?.model?.skinMode === SkinMode.AVIA_UI ) {
							addImport( scriptURL, scriptNamespace );
						}
						break;
					default: // all other avia scripts
						addImport( scriptURL, scriptNamespace );
				}
			}
		);

		imports = this.tag.tagUtilities.processImports( imports ); // final checks

		this?.logger?.log( 'calculateDependencies:', 'dependencies to load:', imports );

		return imports;
	};

}